import { faArrowDown, faArrowUp, faBan, faEllipsisH, faPortrait } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import arrayMove from 'array-move';
import dayjs from 'dayjs';
import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react';
import { toast } from 'react-toastify';
import { Popover, PopoverBody, UncontrolledTooltip } from 'reactstrap';

import { listUserNames, roundToDecimalPlace, scrollToElement } from '../../../../../Common.functions';
import { clone } from '../../../../../Services/schedule';
import style from './AdminRanks.style';
import SortOrderSelector from 'Body/RezRATE/Common/SortOrderSelector/SortOrderSelector';
import Swal from 'sweetalert2';
import { DepartmentContext } from 'DepartmentWrapper';

// Constants
const HEIGHT_MAP = {
  raw: { 1: 30, 2: 60, 3: 70 },
  string: { 1: '30px', 2: '60px', 3: '70px', default: '100px' },
};

const FLAG_OPTIONS = [
  { label: 'No Change', id: 0, icon: faEllipsisH },
  { label: 'Consider Moving Up', value: 'Up', id: 1, icon: faArrowUp, color: '#38824c' },
  { label: 'Consider Moving Down', id: 2, value: 'Down', icon: faArrowDown, color: '#f77777' },
  { label: 'Consider Removing from Ranked List', id: 3, value: 'Remove', icon: faBan, color: '#f77777' },
];

// Utility function for determining height
const determineHeight = (sliderValue, isRaw = false) => {
  if (isRaw) {
    return HEIGHT_MAP.raw[sliderValue] || HEIGHT_MAP.raw[3];
  }
  return HEIGHT_MAP.string[sliderValue] || HEIGHT_MAP.string.default;
};

const AdminRanks = ({
  candidate,
  DragHandle,
  enableRevisedRankScores,
  sliderValue,
  i,
  index,
  candidates,
  setCandidateRanks,
  setSelectedUser,
  selectedUser,
  postChange,
  isFetchingCandidates,
  isUpdatingRankList,
  isCard,
  unique_key,
  setLastJumpTo,
  detailNotesHaveChange,
  onCancelRevisedScoreSave,
  onRevisedScoreChange,
  rowAttributes,
  rowRef,
  rowStyleOverride,
  viewMode,
  mode,
}) => {
  // State management
  const [showMovePopover, setShowMovePopover] = useState(false);
  const [flag, setFlag] = useState(FLAG_OPTIONS[0]);
  const [revisedScoreInput, setRevisedScoreInput] = useState('');

  const { department } = useContext(DepartmentContext) || {};
  const { ProgramType } = department || {};

  // Memoized calculations
  const candidateHeight = useMemo(() => determineHeight(sliderValue, true), [sliderValue]);

  const candidateHeightString = useMemo(() => determineHeight(sliderValue, false), [sliderValue]);

  const isDragDisabled = useMemo(() => isFetchingCandidates || isUpdatingRankList, [
    isFetchingCandidates,
    isUpdatingRankList,
  ]);
  const revisedScoreFieldWidth = 160;

  // Set flag based on candidate consideration
  useEffect(() => {
    if (!candidate) return;

    const { CandidateConsideration } = candidate;
    const flagOption = FLAG_OPTIONS.find((opt) => opt.value === CandidateConsideration) || FLAG_OPTIONS[0];
    setFlag(flagOption);
  }, [candidate?.CandidateConsideration]);

  useEffect(() => {
    const revisedScoreValue = candidate?.RevisedScore;
    setRevisedScoreInput(
      revisedScoreValue === null || revisedScoreValue === undefined || revisedScoreValue === '' ? '' : `${revisedScoreValue}`,
    );
  }, [candidate?.RevisedScore]);

  const revisedScoreDifference = useMemo(() => {
    if (revisedScoreInput === '') {
      return null;
    }

    const parsedRevisedScore = Number(revisedScoreInput);
    if (Number.isNaN(parsedRevisedScore) || parsedRevisedScore < 1 || parsedRevisedScore > 100) {
      return null;
    }

    const finalScore = roundToDecimalPlace(100 * (candidate?.CandidateFinalScoreAsDecimal || 0), 2);
    const difference = roundToDecimalPlace(parsedRevisedScore - finalScore, 2);

    if (difference > 0) {
      return {
        text: `+${difference}`,
        color: '#198754',
      };
    }

    if (difference < 0) {
      return {
        text: `${difference}`,
        color: '#dc3545',
      };
    }

    return {
      text: '0',
      color: '#6c757d',
    };
  }, [candidate?.CandidateFinalScoreAsDecimal, revisedScoreInput]);

  // Callback functions
  const handleSortChange = useCallback(
    (e) => {
      const { value } = e.target;
      const moveCandidate = () => {
        setCandidateRanks((prevState) => {
          const currentCandidate = prevState[i];
          const { FirstName, LastName } = currentCandidate;
          const newOrder = arrayMove(prevState, i, Number(value) - 1);

          setSelectedUser(currentCandidate);
          postChange(
            newOrder,
            prevState,
            `${FirstName} ${LastName} moved from position ${i + 1} to position ${Number(value)}`,
            currentCandidate.pk_Candidate,
            i,
            Number(value) - 1,
            () => {
              setLastJumpTo(i + 1);
              scrollToElement(`row_${currentCandidate.pk_Candidate}_${unique_key}`);
            },
          );
          return newOrder;
        });
      };

      if (detailNotesHaveChange) {
        Swal.fire({
          title: 'Unsaved changes!',
          text:
            'You have unsaved Note changes. Changing Rank order now would permanently discard these changes. Are you sure you want to proceed? This cannot be undone.',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: 'red',
          confirmButtonText: 'Discard',
          cancelButtonColor: 'gray',
          cancelButtonText: 'Cancel',
          reverseButtons: true,
        }).then((result) => {
          if (result.value) moveCandidate();
        });
      } else {
        moveCandidate();
      }
    },
    [i, candidates, setCandidateRanks, setSelectedUser, postChange, setLastJumpTo, unique_key, detailNotesHaveChange],
  );

  const formatFlagMessage = useCallback((LastName, FirstName, oldFlagValue, newFlag) => {
    const getMessageForFlag = (flagValue) => {
      if (flagValue === 'Remove') return FLAG_OPTIONS[3].label;
      if (flagValue === 'Up') return FLAG_OPTIONS[1].label;
      if (flagValue === 'Down') return FLAG_OPTIONS[2].label;
      return 'None';
    };

    const newMessage = getMessageForFlag(newFlag?.value);

    if (!oldFlagValue) {
      return `${LastName}, ${FirstName} flagged as ${newMessage}`;
    }

    const oldMessage = getMessageForFlag(oldFlagValue);
    return `${LastName}, ${FirstName}'s flag changed from ${oldMessage} to ${newMessage}`;
  }, []);

  const handleFlagChange = useCallback(
    (newFlag) => {
      const updateFlag = () => {
        setCandidateRanks((prevState) => {
          const newState = clone(prevState);
          const currentCandidate = newState[i];
          const { FirstName, LastName } = currentCandidate;
          const oldFlagValue = currentCandidate.OriginalCandidateConsideration;

          currentCandidate.Flag = newFlag.id;
          currentCandidate.CandidateConsideration = newFlag.value;

          const message = formatFlagMessage(LastName, FirstName, oldFlagValue, newFlag);
          postChange(newState, prevState, message, currentCandidate.pk_Candidate);

          return newState;
        });
      };

      if (detailNotesHaveChange) {
        Swal.fire({
          title: 'Unsaved changes!',
          text:
            'You have unsaved Note changes. Changing flags now would permanently discard these changes. Are you sure you want to proceed? This cannot be undone.',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: 'red',
          confirmButtonText: 'Discard',
          cancelButtonColor: 'gray',
          cancelButtonText: 'Cancel',
          reverseButtons: true,
        }).then((result) => {
          if (result.value) updateFlag();
        });
      } else {
        updateFlag();
      }
    },
    [i, setCandidateRanks, postChange, formatFlagMessage, detailNotesHaveChange],
  );

  const handleCandidateSelect = useCallback(() => {
    if (detailNotesHaveChange) {
      Swal.fire({
        title: 'Unsaved changes!',
        text:
          'You have unsaved changes in the detail notes. Are you sure you want to discard your changes? This cannot be undone.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: 'red',
        confirmButtonText: 'Discard',
        cancelButtonText: 'Cancel',
      }).then((result) => {
        if (result.isConfirmed) {
          setSelectedUser(candidate);
        }
      });
      return;
    }
    setSelectedUser(candidate);
  }, [candidate, detailNotesHaveChange, setSelectedUser]);

  const handleRevisedScoreInputChange = useCallback((event) => {
    const { value } = event.target;

    if (value === '') {
      setRevisedScoreInput('');
      if (onRevisedScoreChange && candidate?.pk_Candidate) {
        onRevisedScoreChange(candidate.pk_Candidate, null);
      }
      return;
    }

    if (/^\d+$/.test(value)) {
      setRevisedScoreInput(value);

      const parsedValue = Number(value);
      if (
        onRevisedScoreChange &&
        candidate?.pk_Candidate &&
        !Number.isNaN(parsedValue) &&
        parsedValue >= 1 &&
        parsedValue <= 100
      ) {
        onRevisedScoreChange(candidate.pk_Candidate, parsedValue);
      } else if (onCancelRevisedScoreSave && candidate?.pk_Candidate) {
        onCancelRevisedScoreSave(candidate.pk_Candidate);
      }
    }
  }, [candidate?.pk_Candidate, onCancelRevisedScoreSave, onRevisedScoreChange]);

  const handleRevisedScoreBlur = useCallback(() => {
    if (!onRevisedScoreChange || !candidate?.pk_Candidate) {
      return;
    }

    const parsedValue = Number(revisedScoreInput);
    if (revisedScoreInput !== '' && (Number.isNaN(parsedValue) || parsedValue < 1 || parsedValue > 100)) {
      toast.error('Revised Score must be a number from 1 to 100.');
      const revisedScoreValue =
        candidate?.RevisedScore === null || candidate?.RevisedScore === undefined || candidate?.RevisedScore === ''
          ? ''
          : `${candidate.RevisedScore}`;
      setRevisedScoreInput(revisedScoreValue);
    }
  }, [candidate?.RevisedScore, candidate?.pk_Candidate, onRevisedScoreChange, revisedScoreInput]);

  const renderRevisedScoreInput = useCallback(
    (inputStyle = {}) => {
      if (!enableRevisedRankScores) {
        return null;
      }

      return (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: revisedScoreFieldWidth,
          }}
        >
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={revisedScoreInput}
            placeholder={
              revisedScoreInput
                ? revisedScoreInput
                : candidate?.CandidateFinalScoreAsDecimal
                  ? roundToDecimalPlace(100 * candidate.CandidateFinalScoreAsDecimal, 2)
                  : ''
            }
            style={{
              width: 90,
              textAlign: 'center',
              padding: '4px 8px',
              border: '1px solid #ced4da',
              borderRadius: 4,
              backgroundColor: isUpdatingRankList ? '#e9ecef' : 'white',
              ...inputStyle,
            }}
            disabled={isUpdatingRankList}
            onClick={(e) => {
              e.stopPropagation();
            }}
            onChange={handleRevisedScoreInputChange}
            onBlur={handleRevisedScoreBlur}
          />
          <span
            style={{
              minWidth: 40,
              textAlign: 'left',
              color: revisedScoreDifference ? revisedScoreDifference.color : 'transparent',
              fontWeight: 600,
              whiteSpace: 'nowrap',
            }}
          >
            {revisedScoreDifference ? revisedScoreDifference.text : '0'}
          </span>
        </div>
      );
    },
    [
      candidate?.CandidateFinalScoreAsDecimal,
      enableRevisedRankScores,
      handleRevisedScoreInputChange,
      handleRevisedScoreBlur,
      isUpdatingRankList,
      revisedScoreFieldWidth,
      revisedScoreDifference,
      revisedScoreInput,
    ],
  );

  // Render Functions for Popovers and Tooltips
  const renderFlagSelectionPopover = (viewType = 'card') => (
    <Popover
      popperClassName="flagPopover"
      key={`candidate_${pk_Candidate}_${i}_popover`}
      id={`candidate_${pk_Candidate}_${i}_popover`}
      target={`popover_trigger_${pk_Candidate}_${i}_${unique_key}`}
      trigger="legacy"
      flip={false}
      isOpen={showMovePopover}
      toggle={() => setShowMovePopover(!showMovePopover)}
    >
      <PopoverBody>
        <div style={{ ...style.simpleColumn, overflowY: 'scroll' }}>
          {FLAG_OPTIONS.map((flagOption, index) => {
            const backgroundColor =
              (flagOption.id == 0 && flag == null) || (flag && flagOption.id == flag.id)
                ? '#d9f5ff'
                : index % 2 == 0
                ? '#c7cbd1'
                : null;
            return (
              <div
                key={index}
                style={{ ...style.flagItem, backgroundColor, cursor: 'pointer' }}
                onClick={(e) => {
                  e.stopPropagation();
                  setFlag(flagOption);
                  setShowMovePopover(false);
                  handleFlagChange(flagOption);
                }}
              >
                <FontAwesomeIcon
                  icon={flagOption.icon}
                  style={{ marginLeft: 10, fontSize: 20, color: flagOption.color }}
                />{' '}
                <div style={{ textAlign: 'center', width: '100%' }}>{flagOption.label}</div>
              </div>
            );
          })}
        </div>
      </PopoverBody>
    </Popover>
  );

  const renderDoNotRankTooltipWithNames = (targetId, userNames) => (
    <UncontrolledTooltip target={targetId} style={{ width: 150 }}>
      {userNames.map((name, nameIndex) => (
        <div key={nameIndex}>{name}</div>
      ))}
    </UncontrolledTooltip>
  );

  // Early return if candidate is not available
  if (!candidate) {
    return null;
  }

  // Destructure candidate properties
  const {
    pk_Rank,
    FirstName,
    LastName,
    PhotoUrl,
    CandidateFinalScoreAsDecimal = 0,
    DateOfInterview,
    UsersWhoMarkedDoNotRank,
    EvalPosition,
    CandidateSubHeading = '',
    pk_Candidate,
  } = candidate;

  const ApplicantName = `${LastName || ''}${FirstName ? `, ${FirstName}` : ''}`;

  let rowStyle = {
    cursor: 'pointer',
    maxHeight: 40,
    height: `${10 + sliderValue * 10}px !important`,
    // height: sliderValue > 1 ? 'auto' : '30px !important',
  };

  const cellStyle = {
    verticalAlign: 'middle',
    padding: sliderValue > 1 ? '2px' : 2,
    height: 10,
    backgroundColor: 'inherit',
  };

  const formatInterviewDate = (interviewDate) => {
    if (interviewDate == null || interviewDate.trim() === '') {
      return 'No interview date';
    }
    return DateOfInterview ? dayjs(DateOfInterview).format('MM/DD/YYYY') : 'No interview date';
  };

  if (selectedUser && selectedUser.pk_Candidate == pk_Candidate) {
    rowStyle.backgroundColor = '#d9f5ff';
  } else {
    if (isFetchingCandidates || isUpdatingRankList) {
      // rowStyle.backgroundColor = 'red';

      rowStyle.cursor = 'not-allowed';
    }
    if (i % 2 == 0) {
      rowStyle.backgroundColor = '#f2f2f2';
    } else {
      rowStyle.backgroundColor = 'white';
    }
  }

  const mergedRowStyle = rowStyleOverride
    ? {
        ...rowStyle,
        ...rowStyleOverride,
      }
    : rowStyle;

  if (viewMode == 'Grid') {
    // Determine background color for grid item (same logic as table/card views)
    let gridBackgroundColor = 'white';
    if (selectedUser && selectedUser.pk_Candidate == pk_Candidate) {
      gridBackgroundColor = '#d9f5ff';
    } else {
      if (i % 2 == 0) {
        gridBackgroundColor = '#f2f2f2';
      } else {
        gridBackgroundColor = 'white';
      }
    }

    return (
      <div
        id={`row_${pk_Candidate}_${unique_key}`}
        style={{
          ...style.simpleRow,
          cursor: 'grab',
          color: 'black',
          backgroundColor: gridBackgroundColor,
        }}
        onClick={() => {
          if (detailNotesHaveChange) {
            Swal.fire({
              title: 'Unsaved changes!',
              text:
                'You have unsaved changes in the detail notes. Are you sure you want to discard your changes? This cannot be undone.',
              icon: 'warning',
              showCancelButton: true,
              confirmButtonColor: 'red',
              confirmButtonText: 'Discard',
              cancelButtonText: 'Cancel',
            }).then((result) => {
              if (result.isConfirmed) {
                setSelectedUser(candidate);
              }
            });
            return;
          }
          if (selectedUser.pk_Candidate != pk_Candidate) {
            setSelectedUser(candidate);
          }
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: sliderValue > 1 ? 'center' : 'flex-end',
            alignItems: 'center',
            flexDirection: 'column',
            width: sliderValue > 1 ? determineHeight(sliderValue, true) + 10 : '100%',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flexDirection: 'row',
            }}
          >
            <div
              style={{
                position: 'relative',
                width: sliderValue > 1 ? determineHeight(sliderValue) : 45,
                height: sliderValue > 1 ? determineHeight(sliderValue) : 45,
                flexDirection: 'column',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                paddingLeft: sliderValue > 1 ? 10 : 0,
                width: '100%',
              }}
            >
              {PhotoUrl ? (
                <img
                  alt="Candidate Photo"
                  className="rankImage"
                  style={{
                    objectFit: 'cover',
                    float: 'left',
                    width: sliderValue > 1 ? determineHeight(sliderValue) : 45,
                    height: sliderValue > 1 ? determineHeight(sliderValue) : 45,
                  }}
                  src={PhotoUrl}
                  data-holder-rendered="true"
                />
              ) : (
                <FontAwesomeIcon
                  icon={faPortrait}
                  style={{
                    height: '100%',
                    minWidth: sliderValue > 1 ? determineHeight(sliderValue) : 45,
                    fontSize: 30,
                    float: 'center',
                  }}
                />
              )}
            </div>
          </div>
          {sliderValue == 1 && (
            <div
              style={{
                ...style.candidateName,
                fontSize: 11,
                marginLeft: 5,
                textAlign: 'center',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                width: '100%',
              }}
            >
              {ApplicantName}
            </div>
          )}
          {sliderValue == 1 && (
            <div
              className="card-footer"
              style={{
                borderTop: '1px solid black',
                display: 'flex',
                width: '100%',
                justifyContent: 'center',
                alignItems: 'center',
                height: 30,
                backgroundColor: 'gray',
                opacity: 0.8,
              }}
            >
              <div
                style={{
                  ...style.candidateName,
                  fontSize: 19,
                  textAlign: 'center',
                  fontWeight: 'bold',
                }}
              >
                {EvalPosition}
              </div>
            </div>
          )}
        </div>
        {sliderValue > 1 ? (
          <>
            {' '}
            <div
              style={{
                ...style.simpleColumn,
                justifyContent: 'flex-start',
                alignItems: 'left',
                paddingTop: 5,
                paddingRight: 5,
                width:
                  sliderValue > 1
                    ? `calc(100% - ${determineHeight(sliderValue, true) + 40 + (sliderValue - 1) * 10}px)`
                    : '100%',
              }}
            >
              {sliderValue == 2 && (
                <>
                  <div
                    style={{
                      ...style.candidateName,
                      fontSize: 12,
                      marginLeft: 5,
                      fontWeight: 'bold',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {ApplicantName}
                  </div>
                  <div
                    style={{
                      ...style.candidateSchool,
                      fontSize: 11,
                      marginLeft: 5,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {CandidateSubHeading || ''}
                  </div>
                </>
              )}
              {sliderValue === 3 && (
                <>
                  <div
                    style={{
                      ...style.candidateName,
                      fontSize: 13,
                      marginLeft: 5,
                      fontWeight: 'bold',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {ApplicantName}
                  </div>
                  <div
                    style={{
                      ...style.candidateSchool,
                      fontSize: 11,
                      marginLeft: 5,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {CandidateSubHeading || ''}
                  </div>
                </>
              )}
              {sliderValue > 1 && (
                <>
                  <div
                    style={{
                      ...style.candidateName,
                      fontSize: 13,
                      marginLeft: 5,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    Score: {roundToDecimalPlace(100 * CandidateFinalScoreAsDecimal, 2)}
                  </div>

                  {UsersWhoMarkedDoNotRank && UsersWhoMarkedDoNotRank.length > 0 ? (
                    <div
                      id={`DNR_${pk_Rank}`}
                      style={{
                        ...style.candidateSchool,
                        fontSize: 13,
                        marginLeft: 5,
                        fontWeight: 'bold',
                        color: 'red',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {UsersWhoMarkedDoNotRank.length} {mode === 2 ? 'DNI' : 'DNR'}
                      {renderDoNotRankTooltipWithNames(`DNR_${pk_Rank}`, listUserNames(UsersWhoMarkedDoNotRank))}
                    </div>
                  ) : null}
                </>
              )}
            </div>
            <div
              style={{
                ...style.simpleColumn,

                borderLeft: '1px solid black',
                display: 'flex',
                // width: '100%',
                justifyContent: 'center',
                alignItems: 'center',
                width: 40 + (sliderValue - 1) * 10,
                backgroundColor: 'gray',
                opacity: 0.8,
              }}
            >
              <div
                style={{
                  ...style.candidateName,
                  fontSize: sliderValue == 3 ? 19 : 16,
                  textAlign: 'center',
                  fontWeight: 'bold',
                }}
              >
                {EvalPosition}
              </div>
            </div>
          </>
        ) : null}
      </div>
    );
  } else {
    if (isCard) {
      return (
        <div
          id={`row_${pk_Candidate}_${unique_key}`}
          style={{
            display: 'flex',
            flexDirection: 'row',
            border: '1px solid #ddd',
            borderRadius: '8px',
            backgroundColor:
              selectedUser && selectedUser.pk_Candidate == pk_Candidate ? '#d9f5ff' : i % 2 == 0 ? '#f2f2f2' : 'white',
            padding: '10px',
            marginBottom: '8px',
            cursor: 'pointer',
            // minHeight: '80px',
          }}
          onClick={() => {
            if (detailNotesHaveChange) {
              Swal.fire({
                title: 'Unsaved changes!',
                text:
                  'You have unsaved changes in the detail notes. Are you sure you want to discard your changes? This cannot be undone.',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: 'red',
                confirmButtonText: 'Discard',
                cancelButtonText: 'Cancel',
              }).then((result) => {
                if (result.isConfirmed) {
                  setSelectedUser(candidate);
                }
              });
              return;
            }
            if (selectedUser.pk_Candidate != pk_Candidate) {
              setSelectedUser(candidate);
            }
          }}
        >
          {/* Left Column - 160px wide */}
          <div
            style={{
              width: '220px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              alignItems: 'center',
              gap: '10px',
            }}
          >
            {/* First Row: DragHandle and Image */}
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                height: '100%',
                width: '100%',
                justifyContent: 'space-between',
              }}
            >
              <div
                className="dragHandleArea"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignSelf: 'stretch',
                  height: '100%',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'grab',
                }}
              >
                <DragHandle
                  isUpdatingRankList={isUpdatingRankList}
                  isFetchingCandidates={isFetchingCandidates}
                  sliderValue={sliderValue}
                  backgroundColor={
                    'transparent'
                    // selectedUser && selectedUser.pk_Candidate == pk_Candidate
                    //   ? '#d9f5ff'
                    //   : i % 2 == 0
                    //   ? '#f2f2f2'
                    //   : 'white'
                  }
                  pk_Candidate={pk_Candidate}
                />
              </div>
              <div
                style={{
                  position: 'relative',
                  width: '80px',
                  height: '80px',
                }}
              >
                {PhotoUrl ? (
                  <img
                    alt="Candidate Photo"
                    className="rankImage"
                    style={{
                      objectFit: 'cover',
                      width: '80px',
                      height: '80px',
                      borderRadius: '4px',
                    }}
                    src={PhotoUrl}
                    data-holder-rendered="true"
                  />
                ) : (
                  <FontAwesomeIcon
                    icon={faPortrait}
                    style={{
                      width: '80px',
                      height: '80px',
                      fontSize: 50,
                      color: '#999',
                    }}
                  />
                )}
              </div>
            </div>
            {/* Second Row: SortOrderSelector and Flag Button */}
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                width: '100%',
                justifyContent: 'space-between',
              }}
            >
              <div style={{ flex: 1, marginRight: '10px' }}>
                <SortOrderSelector
                  rowIndex={i}
                  itemChoices={candidates}
                  onSortChange={handleSortChange}
                  unique_key={`rank_${pk_Candidate}_${unique_key}`}
                  fontSize={12 + sliderValue}
                  width={'100%'}
                />
              </div>
              <div
                style={{
                  flexShrink: 0,
                  width: 80,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <FontAwesomeIcon
                  id={`popover_trigger_${pk_Candidate}_${i}_${unique_key}`}
                  icon={flag ? flag.icon : faEllipsisH}
                  style={{ fontSize: 20, cursor: 'pointer', color: flag ? flag.color : null }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowMovePopover(true);
                  }}
                />

                {renderFlagSelectionPopover('card')}
              </div>
            </div>
          </div>

          {/* Right Column - Flexible width */}
          <div
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              paddingLeft: '15px',
            }}
          >
            {/* First Row: Candidate Name - Full Width */}
            <div style={{ width: '100%', marginBottom: '10px' }}>
              <div style={{ ...style.candidateName, fontSize: 16, fontWeight: 'bold', marginBottom: '4px' }}>
                {ApplicantName}
              </div>
              {CandidateSubHeading && (
                <div style={{ ...style.candidateSchool, fontSize: 13, color: '#666' }}>{CandidateSubHeading}</div>
              )}
            </div>

            {/* Second Row: All Other Items */}
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: '15px',
              }}
            >
              {/* Interview Date */}
              <div style={{ minWidth: '120px', flex: '0 0 120px' }}>
                <div style={{ fontSize: 12, color: '#666', marginBottom: '2px' }}>Interview Date</div>
                <div style={{ fontSize: 14 }}>{formatInterviewDate(DateOfInterview)}</div>
              </div>

              {/* DNR Count */}
              <div style={{ minWidth: '80px', flex: '0 0 80px' }}>
                <div style={{ fontSize: 12, color: '#666', marginBottom: '2px' }}>{mode === 2 ? 'DNI' : 'DNR'}</div>
                <div style={{ color: UsersWhoMarkedDoNotRank && UsersWhoMarkedDoNotRank.length > 0 ? 'red' : null }}>
                  <b id={`key_${pk_Candidate}_${unique_key}`}>
                    {UsersWhoMarkedDoNotRank ? UsersWhoMarkedDoNotRank.length : 0}
                    {UsersWhoMarkedDoNotRank && UsersWhoMarkedDoNotRank.length > 0
                      ? renderDoNotRankTooltipWithNames(
                          `key_${pk_Candidate}_${unique_key}`,
                          listUserNames(UsersWhoMarkedDoNotRank),
                        )
                      : null}
                  </b>
                </div>
              </div>

              {/* Score */}
              <div style={{ minWidth: '80px', flex: '0 0 80px' }}>
                <div style={{ fontSize: 12, color: '#666', marginBottom: '2px' }}>Score</div>
                <div style={{ fontSize: 16, fontWeight: 'bold' }}>
                  {roundToDecimalPlace(100 * CandidateFinalScoreAsDecimal, 2)}
                </div>
              </div>

              {enableRevisedRankScores ? (
                <div style={{ minWidth: `${revisedScoreFieldWidth}px`, flex: `0 0 ${revisedScoreFieldWidth}px` }}>
                  <div style={{ fontSize: 12, color: '#666', marginBottom: '2px' }}>Revised Score</div>
                  <div>{renderRevisedScoreInput()}</div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      );
    }
    return (
      <tr
        ref={rowRef}
        {...rowAttributes}
        id={`row_${pk_Candidate}_${unique_key}`}
        onClick={() => {
          if (detailNotesHaveChange) {
            Swal.fire({
              title: 'Unsaved changes!',
              text:
                'You have unsaved changes in the detail notes. Are you sure you want to discard your changes? This cannot be undone.',
              icon: 'warning',
              showCancelButton: true,
              confirmButtonColor: 'red',
              confirmButtonText: 'Discard',
              cancelButtonText: 'Cancel',
            }).then((result) => {
              if (result.isConfirmed) {
                setSelectedUser(candidate);
              }
            });
            return;
          }
          if (selectedUser.pk_Candidate != pk_Candidate) {
            setSelectedUser(candidate);
          }
        }}
        style={mergedRowStyle}
      >
        <td style={cellStyle}>
          <div style={{ ...style.simpleRow, alignItems: 'stretch', height: '100%' }}>
            <div
              className="dragHandleArea"
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignSelf: 'stretch',
                height: '100%',
                // marginLeft: 20,
                // marginRight: 20,
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'grab',
              }}
            >
              <DragHandle
                isUpdatingRankList={isUpdatingRankList}
                isFetchingCandidates={isFetchingCandidates}
                sliderValue={sliderValue}
                backgroundColor={mergedRowStyle.backgroundColor}
                pk_Candidate={pk_Candidate}
              />
            </div>
            <div
              style={{
                alignItems: 'center',
                display: 'flex',
                height: '100%',
              }}
            >
              <SortOrderSelector
                rowIndex={i}
                itemChoices={candidates}
                onSortChange={handleSortChange}
                unique_key={`rank_${pk_Candidate}_${unique_key}`}
                fontSize={12 + sliderValue}
              />
            </div>
          </div>
        </td>
        <td style={cellStyle}>
          <div style={{ paddingLeft: 5, paddingRight: 5 }}>
            <FontAwesomeIcon
              id={`popover_trigger_${pk_Candidate}_${i}_${unique_key}`}
              icon={flag ? flag.icon : faEllipsisH}
              style={{ fontSize: 25, cursor: 'pointer', color: flag ? flag.color : null }}
              onClick={(e) => {
                e.stopPropagation();
                setShowMovePopover(true);
              }}
            />

            {renderFlagSelectionPopover('table')}
          </div>
        </td>
        <td style={cellStyle}>
          <span style={{ textAlign: 'left' }}>
            <div style={{ display: 'flex', flexDirection: 'row' }}>
              <div
                style={{
                  position: 'relative',
                  width: determineHeight(sliderValue),
                  height: determineHeight(sliderValue),
                }}
              >
                {PhotoUrl && sliderValue > 1 ? (
                  <img
                    alt="Candidate Photo"
                    className="rankImage"
                    style={{
                      objectFit: 'cover',
                      float: 'left',
                      width: determineHeight(sliderValue),
                      height: determineHeight(sliderValue),
                      marginRight: '10px',
                    }}
                    src={PhotoUrl}
                    data-holder-rendered="true"
                  />
                ) : sliderValue > 1 ? (
                  <FontAwesomeIcon
                    icon={faPortrait}
                    style={{ height: '100%', minWidth: 55, fontSize: 30, float: 'left', marginRight: '10px' }}
                  />
                ) : null}
              </div>
              {sliderValue == 1 && (
                <div style={{ ...style.simpleColumn, verticalAlign: 'middle' }}>
                  <div style={{ ...style.candidateName, marginLeft: 10, textAlign: 'center', fontSize: 14 }}>
                    {ApplicantName}
                  </div>
                </div>
              )}
              {sliderValue == 2 && (
                <div style={{ ...style.simpleColumn, verticalAlign: 'middle' }}>
                  {/* <div>
                  s_pk: {selectedUser.pk_Candidate} c_pk: {pk_Candidate}
                </div> */}
                  <div style={{ ...style.candidateName, fontSize: 14, marginLeft: 10, fontWeight: 'bold' }}>
                    {ApplicantName}
                  </div>
                  <div style={{ ...style.candidateSchool, fontSize: 12, marginLeft: 10 }}>
                    {CandidateSubHeading || ''}
                  </div>
                </div>
              )}

              {sliderValue === 3 && (
                <div style={{ ...style.simpleColumn, verticalAlign: 'middle' }}>
                  <div style={{ ...style.candidateName, fontSize: 16, marginLeft: 10, fontWeight: 'bold' }}>
                    {ApplicantName}
                  </div>
                  <div style={{ ...style.candidateSchool, fontSize: 13, marginLeft: 10 }}>
                    {CandidateSubHeading || ''}
                  </div>
                </div>
              )}
            </div>
          </span>
        </td>
        <td style={cellStyle}>{formatInterviewDate(DateOfInterview)}</td>
        <td style={cellStyle}>
          {' '}
          <div style={{ color: UsersWhoMarkedDoNotRank && UsersWhoMarkedDoNotRank.length > 0 ? 'red' : null }}>
            {' '}
            <b id={`key_${pk_Candidate}_${unique_key}`}>
              {UsersWhoMarkedDoNotRank ? UsersWhoMarkedDoNotRank.length : 0}
              {UsersWhoMarkedDoNotRank && UsersWhoMarkedDoNotRank.length > 0
                ? renderDoNotRankTooltipWithNames(
                    `key_${pk_Candidate}_${unique_key}`,
                    listUserNames(UsersWhoMarkedDoNotRank),
                  )
                : null}
            </b>
          </div>
        </td>
        <td style={cellStyle}>
          <strong>{roundToDecimalPlace(100 * CandidateFinalScoreAsDecimal, 2)}</strong>
        </td>
        {enableRevisedRankScores ? (
          <td style={cellStyle}>
            {renderRevisedScoreInput()}
          </td>
        ) : null}
      </tr>
    );
  }
};

export default AdminRanks;
